import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AuthClient } from './auth-client'
import AuthContext from './auth-context'
import { hasAuthParams } from './auth-utils'
import { reducer } from './auth-reducer'
import { contextOriginService } from './contextOriginService'
import { tokenService } from './tokenService'
import { initialAuthState } from './auth-state'
import { setData, resetStore, setErrors } from '@common/utils/resource'
import analytics from '@helpers/segment'
import Loader from '@app/common/layout/components/Loader'
import { defaultAnonymousUser, getLDUser } from '@helpers/ldHelpers'
import { setLoanIdForEncompass } from '@app/common/socket/actions'
import { getRedirectLink } from '@app/common/utils/helpers'
import * as authErrors from './auth-errors'
import authLogger from './auth-logger'

const defaultOnRedirectCallback = () => {
  window.history.replaceState({}, document.title, window.location.pathname)
}

const defaultSkipAuthCheckCallback = (url = window.location.href) => {
  // Skip auth flow unless the path is /auth/login
  // This allows user activation and reset password to function without triggering auth flow
  try {
    const urlObj = new URL(url)
    return urlObj.pathname !== '/auth/login'
  } catch (error) {
    // If URL parsing fails, default to skipping auth
    authLogger.warn('Error parsing skip auth check', error)
    return true
  }
}

const AuthProvider = (opts) => {
  const {
    children,
    skipAuthCheckCallback = defaultSkipAuthCheckCallback,
    onRedirectCallback = defaultOnRedirectCallback,
    authConfig,
  } = opts
  const storeDispatch = useDispatch()
  const history = useHistory()
  const ldClient = useLDClient()
  const didInitialise = useRef(false)
  const ldTimeoutRef = useRef(null)
  const [state, dispatch] = useReducer(reducer, { ...initialAuthState, ...authConfig })
  const [client] = useState(() => new AuthClient(authConfig.use_session_flow))

  const showErrorMessage = useCallback((message) => {
    storeDispatch(
      setErrors(
        { error: message },
        { resource: { namespace: 'session' } },
      ),
    )
  }, [storeDispatch])

  /**
   * This will log the user in and update state accordingly
   */
  const onAuthenticationSuccess = useCallback((user) => {
    storeDispatch(setData(user, { resource: { namespace: 'user' } }))

    analytics.userLogIn(user)
    if (ldClient) {
      ldClient.identify(getLDUser(user))
    }
    dispatch({ type: 'LOGIN', user })
  }, [dispatch, storeDispatch, ldClient])

  /**
   * This will log the user out and inform the user of the error, this will *not* throw
   */
  const onAuthenticationError = useCallback((error, notify = true) => {
    // TODO: PLAT-2904 Re-evaluate clearing the entire redux store, it only clears the `resource` namespace not all data
    storeDispatch(resetStore())

    analytics.userLogOut()
    if (ldClient) {
      ldClient.identify(defaultAnonymousUser)
    }

    tokenService.removeToken()

    if (notify) {
      if (authErrors.isAuthError(error)) {
        showErrorMessage(error.message)
      } else {
        showErrorMessage('Authentication failed. Please try logging in again.')
      }
    }

    dispatch({ type: 'LOGOUT' })
  }, [ldClient, dispatch, showErrorMessage, storeDispatch])

  /**
   * This will redirect the user to the given URL or the default redirect URL based on logged in permissions
   */
  const redirectUser = useCallback((redirectUrl) => {
    const suppliedRedirectUrl = redirectUrl
    const permissionRedirectUrl = getRedirectLink()
    const finalRedirectUrl = suppliedRedirectUrl ?? permissionRedirectUrl

    authLogger.debug(`auth: redirecting to ${finalRedirectUrl}`)
    history.push(finalRedirectUrl)
  }, [history])

  /**
   * This will log the user in and redirect them to the appropriate page per their permissions
   */
  const login = useCallback(
    async (opts) => {
      try {
        const loginResponse = await client.login(opts)
        tokenService.setToken(loginResponse.accessToken)
        // For now, this checkSession will need to have the tokenService set before calling it
        const sessionResponse = await client.checkSession()
        tokenService.setToken(sessionResponse.accessToken)
        await client.clearUser()
        const user = await client.getUser()
        onAuthenticationSuccess(user)
        redirectUser()
      } catch (error) {
        onAuthenticationError(error)

        const isAuthError = authErrors.isAuthError(error)
        if (isAuthError) {
          throw error
        }

        // Always throw login failed error for any other errors
        throw authErrors.createLoginFailedError(error?.message)
      }
    },
    [client, onAuthenticationSuccess, redirectUser, onAuthenticationError],
  )

  /**
  * This will log the user in and redirect them to the appropriate page per their permissions
  */
  const loginSwitch = useCallback(
    async (opts) => {
      try {
        const loginResponse = await client.login(opts)
        tokenService.setToken(loginResponse.accessToken)
        // For now, this checkSession will need to have the tokenService set before calling it
        const sessionResponse = await client.checkSession()
        tokenService.setToken(sessionResponse.accessToken)
        await client.clearUser()
        const user = await client.getUser()
        onAuthenticationSuccess(user)
        redirectUser()
      } catch (error) {
        // We do not call onAuthenticationError here because we want to keep the user logged in

        const isAuthError = authErrors.isAuthError(error)
        if (isAuthError) {
          throw error
        }

        // Always throw login failed error for any other errors
        throw authErrors.createLoginFailedError(error?.message)
      }
    },
    [client, onAuthenticationSuccess, redirectUser],
  )

  /**
   * This will log the user out and redirect them to the logout page
   */
  const logout = useCallback(async () => {
    try {
      await client.logout()
    } catch (error) {
      authLogger.error('Logout error:', error)
    } finally {
      analytics.userLogOut()
      if (ldClient) {
        ldClient.identify(defaultAnonymousUser)
      }

      tokenService.removeToken()

      dispatch({ type: 'LOGOUT' })
      history.push('/auth/logout')
    }
  }, [client, ldClient, history])

  /**
   * Handle redirect callback handles token based logins and auth code logins
   */
  const handleRedirectCallback = useCallback(async () => {
    try {
      const redirectResult = await client.loginRedirectCallback()
      tokenService.setToken(redirectResult.accessToken)
      const user = await client.getUser()

      if (redirectResult.reggoraOrigin) {
        contextOriginService.setToken(redirectResult.reggoraOrigin)
      }
      if (redirectResult.encompassLoanId) {
        storeDispatch(setLoanIdForEncompass(redirectResult.encompassLoanId))
      }

      onAuthenticationSuccess(user)
      redirectUser(redirectResult.redirectUrl)
    } catch (error) {
      // This flow will not throw since the caller is always this provider module.
      onAuthenticationError(error)
    } finally {
      onRedirectCallback()
    }
  }, [
    client,
    onAuthenticationSuccess,
    onAuthenticationError,
    redirectUser,
    storeDispatch,
    onRedirectCallback,
  ])

  /**
   * Handle existing user login session (silent auth)
   */
  const handleExistingSession = useCallback(async () => {
    try {
      const sessionResponse = await client.checkSession()
      tokenService.setToken(sessionResponse.accessToken)
      const user = await client.getUser()

      onAuthenticationSuccess(user)
    } catch (error) {
      // Existing session errors will log the user out silently
      onAuthenticationError(error, false)
    }
  }, [client, onAuthenticationSuccess, onAuthenticationError])

  const initializeAuth = useCallback(async () => {
    if (didInitialise.current) {
      return
    }
    didInitialise.current = true

    // Clear any existing timeout
    if (ldTimeoutRef.current) {
      clearTimeout(ldTimeoutRef.current)
      ldTimeoutRef.current = null
    }

    authLogger.debug('auth: Initialising provider')

    try {
      if (hasAuthParams() && !skipAuthCheckCallback()) {
        authLogger.debug('auth: logging in externally')
        // Here we'll process the login from query params
        // We'll support processing JWTs (legacy support)
        // And we'll support auth code (new flow)
        await handleRedirectCallback()
      } else {
        authLogger.debug('auth: checking existing session')
        // Here we'll process the current user session
        // Which will start with a local storage JWT (for legacy support)
        // Will perform silent authentication to hydrate a fresh JWT off the session (new flow)
        await handleExistingSession()
      }
    } catch (error) {
      // Any error that reaches here considers the user as not authenticated
      dispatch({ type: 'NO_USER' })
    }
  }, [handleExistingSession, handleRedirectCallback, skipAuthCheckCallback])

  const contextValue = {
    ...state,
    login,
    loginSwitch,
    logout,
  }

  useEffect(() => {
    if (didInitialise.current) {
      return
    }

    // Set up timeout to proceed without LaunchDarkly after .75 seconds
    ldTimeoutRef.current = setTimeout(() => {
      authLogger.warn('auth: LaunchDarkly timeout, proceeding without it')
      initializeAuth()
    }, 750)

    // If ldClient is available, proceed immediately
    if (ldClient) {
      clearTimeout(ldTimeoutRef.current)
      ldTimeoutRef.current = null
      initializeAuth()
    }

    // Cleanup timeout on unmount
    return () => {
      if (ldTimeoutRef.current) {
        clearTimeout(ldTimeoutRef.current)
        ldTimeoutRef.current = null
      }
    }
  }, [
    ldClient,
    initializeAuth,
  ])

  // Don't render children until auth is initialized
  if (!state.isInitialized) {
    return (
      <Loader />
    )
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export default AuthProvider
